import { fetchApi } from './client';
import type { PaginatedResponse } from './types';

/** One row in the vendor-portal admin allowlist (VGS `admin_users`). */
export interface AdminUser {
    id: number;
    email: string;
    created_by: string | null;
    created_at: string;
}

/**
 * Whether the current Cloudflare Access caller is a vendor-portal admin.
 *
 * VGS `GET /api/admin-users/me` is the show/hide signal for the admin section:
 * it returns `is_admin: true` in every non-prod environment (the admin guard is
 * a pass-through there); in prod it reflects the `admin_users` allowlist. The CF
 * Access identity endpoint carries no admin signal, so this must come from VGS.
 */
export async function fetchAmIAdmin(): Promise<{ is_admin: boolean }> {
    return fetchApi<{ is_admin: boolean }>('/api/admin-users/me');
}

/** List the admin allowlist. Admin-gated in prod. */
export async function listAdminUsers(
    params: { limit?: number; offset?: number } = {},
): Promise<PaginatedResponse<AdminUser>> {
    const searchParams = new URLSearchParams();
    if (params.limit !== undefined) {
        searchParams.set('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
        searchParams.set('offset', String(params.offset));
    }
    const queryString = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminUser>>(
        `/api/admin-users${queryString ? `?${queryString}` : ''}`,
    );
}

/** Create (promote) an admin by email. Admin-gated in prod. */
export async function createAdminUser(email: string): Promise<AdminUser> {
    return fetchApi<AdminUser>('/api/admin-users', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

/** Delete (demote) an admin by row id. Admin-gated in prod. */
export async function deleteAdminUser(id: number): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>(`/api/admin-users/${String(id)}`, {
        method: 'DELETE',
    });
}
